import React, { useState, useEffect } from 'react';
import { tasksAPI, usersAPI } from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Send, 
  Sparkles, 
  CheckCircle2,
  Clock,
  AlertTriangle,
  Target,
  ChevronRight,
  Loader2,
  Trash2,
  ListChecks,
  CheckSquare,
  Square,
  User,
  UserPlus,
  X
} from 'lucide-react';

const TaskDetailModal = ({ task, open, onClose, onUpdate, onDelete }) => {
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [manualProgress, setManualProgress] = useState(task?.progress || 0);
  const [showManualEdit, setShowManualEdit] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Checklist state
  const [checklist, setChecklist] = useState([]);
  const [generatingChecklist, setGeneratingChecklist] = useState(false);
  const [checklistTips, setChecklistTips] = useState('');
  const [checklistTime, setChecklistTime] = useState('');
  
  // Assignment state
  const [taskAssignee, setTaskAssignee] = useState(null);

  useEffect(() => {
    if (task && open) {
      fetchComments();
      fetchUsers();
      setManualProgress(task.progress || 0);
      setChecklist(task.checklist || []);
      setChecklistTips(task.checklist_tips || '');
      setChecklistTime(task.checklist_estimated_time || '');
      setTaskAssignee(task.assignee_id ? { 
        user_id: task.assignee_id, 
        name: task.assignee_name 
      } : null);
    }
  }, [task, open]);

  const fetchComments = async () => {
    if (!task) return;
    try {
      setLoading(true);
      const response = await tasksAPI.getComments(task.task_id);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setSubmitting(true);
      const response = await tasksAPI.addComment(task.task_id, newComment);
      
      toast.success(`Progreso actualizado: ${response.data.suggested_progress}%`);
      setNewComment('');
      fetchComments();
      
      if (onUpdate) {
        onUpdate({
          ...task,
          progress: response.data.suggested_progress,
          status: response.data.task_status
        });
      }
    } catch (error) {
      toast.error('Error al agregar comentario');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualProgressUpdate = async () => {
    try {
      const response = await tasksAPI.updateProgress(task.task_id, manualProgress);
      toast.success('Progreso actualizado manualmente');
      setShowManualEdit(false);
      
      if (onUpdate) {
        onUpdate(response.data);
      }
    } catch (error) {
      toast.error('Error al actualizar progreso');
    }
  };

  const handleDeleteTask = async () => {
    try {
      setDeleting(true);
      await tasksAPI.delete(task.task_id);
      toast.success('Tarea eliminada');
      setShowDeleteDialog(false);
      if (onDelete) {
        onDelete(task.task_id);
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar tarea');
    } finally {
      setDeleting(false);
    }
  };

  const handleAssignTask = async (userId) => {
    try {
      const response = await tasksAPI.assign(task.task_id, userId);
      setTaskAssignee(response.data.assignee);
      toast.success(`Tarea asignada a ${response.data.assignee.name}`);
      
      if (onUpdate) {
        onUpdate(response.data.task);
      }
    } catch (error) {
      toast.error('Error al asignar tarea');
    }
  };

  const handleUnassignTask = async () => {
    try {
      const response = await tasksAPI.unassign(task.task_id);
      setTaskAssignee(null);
      toast.success('Asignación removida');
      
      if (onUpdate) {
        onUpdate(response.data);
      }
    } catch (error) {
      toast.error('Error al remover asignación');
    }
  };

  const handleGenerateChecklist = async () => {
    try {
      setGeneratingChecklist(true);
      const response = await tasksAPI.generateChecklist(task.task_id);
      setChecklist(response.data.checklist || []);
      setChecklistTips(response.data.tips || '');
      setChecklistTime(response.data.estimated_time || '');
      toast.success('Checklist generada');
      
      if (onUpdate) {
        onUpdate({
          ...task,
          checklist: response.data.checklist,
          checklist_tips: response.data.tips,
          checklist_estimated_time: response.data.estimated_time
        });
      }
    } catch (error) {
      toast.error('Error al generar checklist');
    } finally {
      setGeneratingChecklist(false);
    }
  };

  const handleToggleChecklistItem = async (itemId, completed) => {
    try {
      const response = await tasksAPI.toggleChecklistItem(task.task_id, itemId, !completed);
      setChecklist(response.data.checklist);
      
      if (onUpdate) {
        onUpdate({
          ...task,
          checklist: response.data.checklist,
          progress: response.data.progress,
          status: response.data.progress >= 100 ? 'completed' : task.status
        });
      }
      
      if (response.data.progress >= 100) {
        toast.success('¡Tarea completada! 🎉');
      }
    } catch (error) {
      toast.error('Error al actualizar checklist');
    }
  };

  const handleAssignChecklistItem = async (itemId, userId) => {
    try {
      const response = await tasksAPI.assignChecklistItem(task.task_id, itemId, userId);
      setChecklist(response.data.checklist);
      toast.success(`Item asignado a ${response.data.assignee.name}`);
      
      if (onUpdate) {
        onUpdate({
          ...task,
          checklist: response.data.checklist
        });
      }
    } catch (error) {
      toast.error('Error al asignar item');
    }
  };

  const handleUnassignChecklistItem = async (itemId) => {
    try {
      const response = await tasksAPI.unassignChecklistItem(task.task_id, itemId);
      setChecklist(response.data.checklist);
      toast.success('Asignación removida');
      
      if (onUpdate) {
        onUpdate({
          ...task,
          checklist: response.data.checklist
        });
      }
    } catch (error) {
      toast.error('Error al remover asignación');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'blocked':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-emerald-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-amber-500';
    return 'bg-gray-300';
  };

  const UserAvatar = ({ name, size = 'sm' }) => {
    const sizeClasses = size === 'sm' ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm';
    return (
      <div className={`${sizeClasses} rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-medium`}>
        {name?.charAt(0)?.toUpperCase()}
      </div>
    );
  };

  if (!task) return null;

  const completedItems = checklist.filter(item => item.completed).length;
  const totalItems = checklist.length;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-gray-100 pb-4">
            <div className="flex items-start gap-3">
              {getStatusIcon(task.status)}
              <div className="flex-1">
                <DialogTitle className="text-gray-900 text-lg">{task.title}</DialogTitle>
                {task.description && (
                  <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowDeleteDialog(true)}
                className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                data-testid="delete-task-btn"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4 space-y-6">
            {/* Task Assignee Section */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Responsable de la Tarea</span>
                </div>
                
                {taskAssignee ? (
                  <div className="flex items-center gap-2">
                    <UserAvatar name={taskAssignee.name} />
                    <span className="text-sm font-medium text-gray-900">{taskAssignee.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleUnassignTask}
                      className="h-6 w-6 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Select onValueChange={handleAssignTask}>
                    <SelectTrigger className="w-48 h-9 bg-white border-blue-200">
                      <SelectValue placeholder="Asignar a..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          <div className="flex items-center gap-2">
                            <UserAvatar name={user.name} />
                            <span>{user.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Progress Section */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">Progreso</span>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-gray-900">{task.progress || 0}%</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowManualEdit(!showManualEdit)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Editar
                  </Button>
                </div>
              </div>
              
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${getProgressColor(task.progress || 0)}`}
                  style={{ width: `${task.progress || 0}%` }}
                />
              </div>

              {showManualEdit && (
                <div className="mt-3 flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={manualProgress}
                    onChange={(e) => setManualProgress(parseInt(e.target.value) || 0)}
                    className="w-24 bg-white border-gray-200"
                  />
                  <span className="text-gray-500">%</span>
                  <Button
                    size="sm"
                    onClick={handleManualProgressUpdate}
                    className="bg-gray-900 hover:bg-gray-800 text-white"
                  >
                    Guardar
                  </Button>
                </div>
              )}
            </div>

            {/* Definition of Done */}
            {task.definition_of_done && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Definition of Done</span>
                </div>
                <p className="text-sm text-amber-700">{task.definition_of_done}</p>
              </div>
            )}

            {/* Checklist Section - Trello Style with Assignments */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-purple-500" />
                  <span className="text-sm font-medium text-gray-900">Checklist de Acciones</span>
                  {totalItems > 0 && (
                    <span className="text-xs text-gray-500">
                      ({completedItems}/{totalItems})
                    </span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateChecklist}
                  disabled={generatingChecklist}
                  className="border-purple-200 text-purple-600 hover:bg-purple-50"
                  data-testid="generate-checklist-btn"
                >
                  {generatingChecklist ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {checklist.length > 0 ? 'Regenerar' : 'Generar con IA'}
                    </>
                  )}
                </Button>
              </div>

              {checklist.length > 0 ? (
                <div className="space-y-2">
                  {/* Progress bar for checklist */}
                  <div className="mb-4">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 transition-all duration-300"
                        style={{ width: `${totalItems > 0 ? (completedItems / totalItems) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  
                  {checklist.map((item) => (
                    <div 
                      key={item.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        item.completed 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-white border-gray-100 hover:bg-gray-50'
                      }`}
                      data-testid={`checklist-item-${item.id}`}
                    >
                      {/* Checkbox */}
                      <button
                        onClick={() => handleToggleChecklistItem(item.id, item.completed)}
                        className="flex-shrink-0"
                      >
                        {item.completed ? (
                          <CheckSquare className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                        )}
                      </button>
                      
                      {/* Text */}
                      <span className={`text-sm flex-1 ${
                        item.completed ? 'text-gray-400 line-through' : 'text-gray-700'
                      }`}>
                        {item.text}
                      </span>
                      
                      {/* Assignee */}
                      {item.assignee_id ? (
                        <div className="flex items-center gap-1">
                          <UserAvatar name={item.assignee_name} />
                          <span className="text-xs text-gray-500 hidden sm:inline">{item.assignee_name?.split(' ')[0]}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnassignChecklistItem(item.id);
                            }}
                            className="h-5 w-5 text-gray-400 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-gray-400 hover:text-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border-gray-200 w-48">
                            {users.map((user) => (
                              <DropdownMenuItem
                                key={user.user_id}
                                onClick={() => handleAssignChecklistItem(item.id, user.user_id)}
                                className="cursor-pointer"
                              >
                                <div className="flex items-center gap-2">
                                  <UserAvatar name={user.name} />
                                  <span className="text-sm">{user.name}</span>
                                </div>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
                  
                  {/* Tips and estimated time */}
                  {(checklistTips || checklistTime) && (
                    <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                      {checklistTime && (
                        <div className="flex items-center gap-2 text-xs text-purple-700 mb-1">
                          <Clock className="w-3 h-3" />
                          <span>Tiempo estimado: {checklistTime}</span>
                        </div>
                      )}
                      {checklistTips && (
                        <p className="text-xs text-purple-600">💡 {checklistTips}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <ListChecks className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Sin checklist</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Genera una checklist con IA para ver los pasos específicos
                  </p>
                </div>
              )}
            </div>

            {/* Comments Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Comentarios ({comments.length})
                </span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : comments.length > 0 ? (
                <div className="space-y-4 max-h-[200px] overflow-y-auto">
                  {comments.map((comment) => (
                    <div 
                      key={comment.comment_id}
                      className="border border-gray-100 rounded-lg p-4 bg-white"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <UserAvatar name={comment.author_name} />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{comment.author_name}</p>
                            <p className="text-xs text-gray-400">
                              {new Date(comment.created_at).toLocaleString('es-ES')}
                            </p>
                          </div>
                        </div>
                        {comment.progress_before !== comment.progress_after && (
                          <div className="flex items-center gap-1 text-xs">
                            <span className="text-gray-400">{comment.progress_before}%</span>
                            <ChevronRight className="w-3 h-3 text-gray-400" />
                            <span className="text-emerald-600 font-medium">{comment.progress_after}%</span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-3">{comment.content}</p>
                      
                      {comment.ai_analysis && (
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 rounded-lg p-3 mt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-purple-500" />
                            <span className="text-xs font-medium text-purple-700">Análisis IA</span>
                          </div>
                          <p className="text-sm text-gray-700">{comment.ai_analysis}</p>
                          
                          {comment.ai_next_steps && comment.ai_next_steps.length > 0 && (
                            <div className="mt-3">
                              <span className="text-xs font-medium text-purple-700">Próximos pasos:</span>
                              <ul className="mt-1 space-y-1">
                                {comment.ai_next_steps.map((step, idx) => (
                                  <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                                    <span className="text-purple-400">•</span>
                                    {step}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No hay comentarios aún</p>
                </div>
              )}
            </div>
          </div>

          {/* New Comment Input */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-gray-500">
                La IA analizará tu comentario y actualizará el progreso automáticamente
              </span>
            </div>
            <Textarea
              placeholder="Describe qué has avanzado en esta tarea..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="w-full bg-gray-50 border-gray-200 focus:bg-white min-h-[80px] resize-none text-base"
              data-testid="task-comment-input"
            />
            <div className="flex justify-end mt-4">
              <Button
                onClick={handleSubmitComment}
                disabled={submitting || !newComment.trim()}
                className="action-button-primary bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 min-w-[160px]"
                data-testid="submit-comment-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Enviar
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Eliminar Tarea</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar esta tarea? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="p-4 bg-gray-50 rounded-lg my-4">
            <p className="text-sm font-medium text-gray-900">{task.title}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-200">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTask}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TaskDetailModal;
